import { Flex } from '@amzn/ring-ui-react-components';
import { ChangeEvent, useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Header,
  VideoPlayer,
  VideoPlayerHeader,
} from 'src/components/Posting/components/RingHistory';

import { usePostingWizard } from 'src/components/Posting/hooks';
import {
  Device,
  DeviceSelectorDropdown,
} from 'src/shared/components/DeviceSelectorDropdown';
import { DeviceSelectorModal } from 'src/shared/components/DeviceSelectorModal';
import {
  Location,
  LocationSelectorDropdown,
} from 'src/shared/components/LocationSelectorDropdown';
import { LocationSelectorModal } from 'src/shared/components/LocationSelectorModal';
import { MediaItemList, Video } from 'src/shared/components/MediaItemList';
import { MAX_LIMIT_FILES } from 'src/shared/components/MediaUploader';
import { NoMediaSelected } from 'src/shared/components/NoMediaSelected';
import { NoSubscriptionModal } from 'src/shared/components/NoSubscriptionModal';
import { toast } from 'src/shared/components/Toast';
import { VerticalDivider } from 'src/shared/components/VerticalDivider';
import { useIsMobile } from 'src/shared/hooks';
import { PostingContainer, PostingContent } from '../components';
import {
  useGetVideosByLocationId,
  useGetVideosByDeviceId,
  useGetDevicesByLocationId,
  defaultDevice,
  useGetRingLocations,
} from '../hooks';

export const checkSubscriptionWithToggler = (
  device: Device,
  devicesList: Device[],
  toggler: () => void,
) => {
  if (!toggler) return;
  // Validate all devices
  if (device.id === 'all-cameras' && devicesList.length > 1) {
    let camerasWitoutSubscription = 0;
    devicesList.forEach((device: Device) => {
      if (!device.isRecordingEnabled && device.id !== 'all-cameras') {
        camerasWitoutSubscription++;
      }
    });
    // devicesList.length - 1 ===> Ignoring the default "All Cameras" item
    if (camerasWitoutSubscription === devicesList.length - 1) {
      // Means all devices have not subscription
      toggler();
    }
  }
  // Validate one specific device
  if (device.id !== 'all-cameras' && !device.isRecordingEnabled) {
    toggler();
  }
};

export const RingHistoryStep = () => {
  const { t } = useTranslation('components.posting');
  const [
    {
      context: { isNoSubscriptionModalOpen, media, selectedMedia, ringHistory },
    },
    { send },
  ] = usePostingWizard();
  const isMobile = useIsMobile();
  const { data: ringLocations } = useGetRingLocations({
    onCompleted(data: Array<Location>) {
      setSelectedLocationId(data[0].id);
      setSelectedLocationName(data[0].name);
    },
  });
  const [selectedLocationId, setSelectedLocationId] =
    useState<string | null>(null);
  const [selectedLocationName, setSelectedLocationName] =
    useState<string | null>(null);
  const {
    data: locationVideos,
    loading: loadingLocationVideos,
    fetchMore: locationFetchMore,
  } = useGetVideosByLocationId(selectedLocationId);
  const deviceListQuery = useGetDevicesByLocationId(selectedLocationId);
  const { data: devicesList } = deviceListQuery;
  const [selectedDevice, setSelectedDevice] = useState<Device>(defaultDevice);

  const {
    data: deviceVideos,
    loading: loadingDeviceVideos,
    fetchMore: deviceFetchMore,
  } = useGetVideosByDeviceId(selectedDevice.id);

  const loadingVideos = useMemo(
    () => loadingLocationVideos || loadingDeviceVideos,
    [loadingLocationVideos, loadingDeviceVideos],
  );

  const videos = useMemo(() => {
    let listOfVideos = deviceVideos.videos;
    if (selectedDevice.id === defaultDevice.id)
      listOfVideos = locationVideos.videos;

    return listOfVideos.filter(
      (video: Video) => video.hasSubscription && !!video.url,
    );
  }, [selectedDevice, locationVideos, deviceVideos]);

  const fetchMore = useMemo(
    () =>
      selectedDevice.id === defaultDevice.id
        ? locationFetchMore
        : deviceFetchMore,
    [selectedDevice.id, locationFetchMore, deviceFetchMore],
  );

  const hasNextPage = useMemo(
    () =>
      selectedDevice.id === defaultDevice.id
        ? locationVideos.hasNextPage
        : deviceVideos.hasNextPage,
    [selectedDevice.id, deviceVideos.hasNextPage, locationVideos.hasNextPage],
  );

  const endCursor = useMemo(
    () =>
      selectedDevice.id === defaultDevice.id
        ? locationVideos.endCursor
        : deviceVideos.endCursor,
    [selectedDevice.id, deviceVideos.endCursor, locationVideos.endCursor],
  );

  const locationName = useMemo(
    () =>
      ringLocations?.find((location) => location.id === selectedLocationId)
        ?.name,
    [selectedLocationId, ringLocations],
  );

  const defaultVideo: Video = useMemo(() => {
    const historyVideos = media.filter((m) => !(m instanceof File));
    return (historyVideos[historyVideos.length - 1] as Video) || videos[0];
  }, [media, videos]);

  const [playingVideo, setPlayingVideo] = useState(defaultVideo);

  const handleVideoCheckboxChange = useCallback(
    (video: Video) => (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        send({
          type: 'EVENT_HISTORY_MEDIA_SELECTED',
          payload: [video],
        });
      } else {
        const index = media.findIndex((m) => (m as Video).id === video.id);
        send({
          type: 'EVENT_HISTORY_MEDIA_DELETED',
          payload: index,
        });
      }
    },
    [media, send],
  );

  const handleDeviceSelectorModalClose = useCallback(() => {
    send({
      type: 'EVENT_HISTORY_CHANGE_CAMERAS_CLICKED',
      payload: {
        isChangeCamerasModalOpen: false,
      },
    });
  }, [send]);

  const handleChangeCamerasClick = useCallback(() => {
    send({
      type: 'EVENT_HISTORY_CHANGE_CAMERAS_CLICKED',
      payload: {
        isChangeCamerasModalOpen: true,
      },
    });
  }, [send]);

  const handleLocationSelectorModalClose = useCallback(() => {
    send({
      type: 'EVENT_HISTORY_CHANGE_LOCATION_CLICKED',
      payload: {
        isChangeLocationModalOpen: false,
      },
    });
  }, [send]);

  const handleLocationSelectorModalOpen = useCallback(() => {
    send({
      type: 'EVENT_HISTORY_CHANGE_LOCATION_CLICKED',
      payload: {
        isChangeLocationModalOpen: true,
      },
    });
  }, [send]);

  const toggleNoSubscriptionModal = useCallback(() => {
    send({
      type: 'TOGGLE_NO_SUBSCRIPTION_MODAL',
    });
  }, [send]);

  const checkDeviceSubscription = useCallback(
    (device: Device) =>
      checkSubscriptionWithToggler(
        device,
        devicesList,
        toggleNoSubscriptionModal,
      ),
    [devicesList, toggleNoSubscriptionModal],
  );

  useEffect(() => {
    if (selectedLocationId !== null) {
      checkDeviceSubscription(selectedDevice);
    }
  }, [selectedDevice, selectedLocationId, checkDeviceSubscription]);

  const handleDeviceChange = useCallback(
    (id: string) => {
      const selectedDevice = devicesList.find(
        (device: Device) => device.id === id,
      );
      setSelectedDevice(selectedDevice);
    },
    [setSelectedDevice, devicesList],
  );

  const handleLocationChange = useCallback(
    (id: string) => {
      setSelectedDevice(defaultDevice);
      setSelectedLocationId(id);
    },
    [setSelectedLocationId, setSelectedDevice],
  );

  const handleOnLoadMoreItems = useCallback(() => {
    fetchMore({
      variables: { after: endCursor },
      updateQuery: (prevResult, options) => {
        const { fetchMoreResult } = options;
        if (!fetchMoreResult) return prevResult;
        const objectKey =
          selectedDevice.id === defaultDevice.id
            ? 'ringVideosByLocationId'
            : 'ringVideosByDeviceId';

        return {
          [objectKey]: {
            ...(fetchMoreResult as any)?.[objectKey],
            edges: [
              ...(prevResult as unknown as any)?.[objectKey]?.edges,
              ...(fetchMoreResult as unknown as any)?.[objectKey]?.edges,
            ],
          },
        };
      },
    });
  }, [endCursor, fetchMore, selectedDevice]);

  const FILES_FROM_LOCAL_STORAGE = media.length;
  const HISTORY_LIMIT = MAX_LIMIT_FILES - FILES_FROM_LOCAL_STORAGE;

  const handlePreviewError = () => {
    toast.error(t('video-preview-error'));
  };

  const handleVideoPreview = useCallback(
    (videoId) => () => {
      const selectedVideo = videos.find((video: Video) => video.id === videoId);
      if (selectedVideo) {
        setPlayingVideo(selectedVideo);
      }
    },
    [videos],
  );

  return (
    <PostingContainer pb={0} maxWidth="none">
      <Flex
        flexDirection={{ base: 'column-reverse', m: 'row' }}
        width="full"
        height={{ base: 'unset', m: 'calc(100vh - 144px)' }}
      >
        <Flex flexDirection="row">
          <Flex
            flexDirection="column"
            width={{ base: 'full', m: '25vw' }}
            mr={{ base: 0, m: 8 }}
            mt={{ base: 0, m: 8 }}
          >
            {isMobile ? null : <Header limit={HISTORY_LIMIT} />}
            <PostingContent
              mb={{ base: 0, m: 0 }}
              width={{ base: 'full', m: 'full' }}
              minHeight={0}
              flexGrow={1}
            >
              <Flex flexDirection="column" height="100%">
                {isMobile ? (
                  <>
                    <LocationSelectorModal
                      isOpen={ringHistory.isChangeLocationModalOpen}
                      onClose={handleLocationSelectorModalClose}
                      locations={ringLocations || []}
                      onChange={handleLocationChange}
                      selectedLocation={selectedLocationId!}
                    />
                    <DeviceSelectorModal
                      isOpen={ringHistory.isChangeCamerasModalOpen}
                      onClose={handleDeviceSelectorModalClose}
                      devices={devicesList}
                      onChange={handleDeviceChange}
                      selectedDevice={selectedDevice.id}
                    />
                  </>
                ) : (
                  <>
                    <LocationSelectorDropdown
                      locations={ringLocations || []}
                      onClick={handleLocationChange}
                      selectedLocation={selectedLocationId!}
                    />
                    <DeviceSelectorDropdown
                      devices={devicesList}
                      onClick={handleDeviceChange}
                      selectedDevice={selectedDevice.id}
                    />
                  </>
                )}
                <MediaItemList
                  items={videos}
                  isLoading={loadingVideos}
                  onChange={handleVideoCheckboxChange}
                  select={handleVideoPreview}
                  selected={media as Video[]}
                  previewing={playingVideo}
                  selectedMediaAmount={selectedMedia}
                  onLoadMoreItems={handleOnLoadMoreItems}
                  hasNextPage={hasNextPage}
                />
              </Flex>
            </PostingContent>
          </Flex>
          {isMobile ? null : <VerticalDivider mr={10} />}
        </Flex>
        <Flex flexDirection="column" width="100%">
          {isMobile ? <Header limit={HISTORY_LIMIT} /> : null}
          <VideoPlayerHeader
            isMobile={isMobile}
            handleChangeCamerasClick={handleChangeCamerasClick}
            handleChangeLocationClick={handleLocationSelectorModalOpen}
            hasLocationSelector={ringLocations!.length >= 2}
            device={selectedDevice!.name}
            location={locationName}
            previewDeviceName={playingVideo?.deviceName ?? selectedDevice?.name}
          />
          {playingVideo && (
            <VideoPlayer
              url={playingVideo.url}
              handleError={handlePreviewError}
            />
          )}
          {!playingVideo && videos?.length > 0 && (
            <NoMediaSelected limit={HISTORY_LIMIT} />
          )}
        </Flex>
      </Flex>
      <NoSubscriptionModal
        isOpen={isNoSubscriptionModalOpen}
        onClose={toggleNoSubscriptionModal}
        locationId={selectedLocationId}
        locationName={selectedLocationName}
        selectedDevice={selectedDevice}
      />
    </PostingContainer>
  );
};
